import { Controller } from "@hotwired/stimulus"

// Password input with strength meter + show/hide toggle.
//
// Strength heuristic (0–4):
//   length>=8 +1, length>=12 +1, has letter+digit +1, has symbol +1.
// Same scoring zxcvbn uses for the simple meter.
//
// A11y: o `label` recebe aria-live="polite" pra anunciar a mudança de força
// ("Fraca → Boa → Forte") sem interromper o usuário enquanto digita.
export default class extends Controller {
  static targets = ["input", "bar", "label", "toggleOn", "toggleOff"]
  static values  = {
    min:        { type: Number, default: 8 },
    // Labels traduzíveis vindas do partial; default em pt-BR pra não quebrar
    // consumidores legados que não passam os valores.
    labelEmpty: { type: String, default: "—" },
    labelWeak:  { type: String, default: "Fraca" },
    labelFair:  { type: String, default: "Razoável" },
    labelGood:  { type: String, default: "Boa" },
    labelStrong:{ type: String, default: "Forte" }
  }

  connect() {
    // Garante live region antes do primeiro update().
    if (this.hasLabelTarget) {
      this.labelTarget.setAttribute("aria-live", "polite")
      this.labelTarget.setAttribute("aria-atomic", "true")
    }
    this.update()
  }

  update() {
    const value = this.inputTarget.value
    const score = this.score(value)
    const { width, color, label } = this.meta(score, value.length)

    // bars/label só existem quando o partial é renderizado com strength: true.
    // Em strength: false (ex: password_confirmation), o controller ainda monta
    // pro toggle de mostrar/ocultar funcionar — guards evitam crash.
    this.barTargets.forEach((bar, i) => {
      bar.className = `h-1 flex-1 rounded-full transition-colors ${i < width ? color : "bg-muted"}`
    })
    if (this.hasLabelTarget) {
      this.labelTarget.textContent = label
      this.labelTarget.className   = `text-xs font-medium transition-colors ${value.length ? color.replace("bg-", "text-") : "text-muted-foreground"}`
    }
  }

  toggle() {
    const showing = this.inputTarget.type === "text"
    this.inputTarget.type = showing ? "password" : "text"
    this.toggleOnTarget.classList.toggle("hidden", !showing)
    this.toggleOffTarget.classList.toggle("hidden", showing)
  }

  score(value) {
    let s = 0
    if (value.length >= this.minValue) s++
    if (value.length >= 12)            s++
    if (/[A-Za-z]/.test(value) && /\d/.test(value)) s++
    if (/[^A-Za-z0-9]/.test(value))    s++
    return s
  }

  meta(score, length) {
    if (length === 0)        return { width: 0, color: "bg-muted",         label: this.labelEmptyValue }
    if (score <= 1)          return { width: 1, color: "bg-destructive",   label: this.labelWeakValue }
    if (score === 2)         return { width: 2, color: "bg-orange-500",    label: this.labelFairValue }
    if (score === 3)         return { width: 3, color: "bg-yellow-500",    label: this.labelGoodValue }
    return                          { width: 4, color: "bg-green-500",     label: this.labelStrongValue }
  }
}
